import { Input } from '@alfa-retail-react/components';
import classNames from 'classnames';
import React from 'react';
import { Controller } from 'react-hook-form';
import { observer } from 'mobx-react';
import { useFormContext } from '@alfa-retail-react/components/form-hook';

import { WEB_VISOR_DISABLE } from 'app/utils/AnalyticsUtils';
import { MAX_LENGTH_LATIN, MIN_LENGTH_LATIN } from 'app/constants/baseConstants';
import { InputFieldEventHandler } from 'app/form/InputFieldEventHandler';
import { OrderFormValues } from 'app/logic/pages/order/OrderPage';
import {
    getFIOAllowedCharactersForFirstAndLastSymbolValidation,
    getFIOAllowedCharactersValidation,
    getFIOMaxLengthValidation,
    getFIOMinLengthValidation,
    ValidationRules,
} from 'app/form/ValidationRules';

/**
 * Удаляет пробелы с двух сторон у значения.
 */
const removeSpaces = (field: string): string => {
    return field?.trim();
};

type TStoreItemName = 'surnameLatin' | 'nameLatin';

export interface LatinFormItemProps {
    defaultValue: string;
    placeholder: string;

    /**
     * Имя переменной в хранилище, хранящей поле на латинице
     */
    formItemName: TStoreItemName;
    onChangeFormItemValue(newValue: string): void;
}

/**
 * Компонент для отображения элемента формы имени/фамилии на латинице.
 */
export const LatinFormItem = observer(
    ({
        defaultValue,
        placeholder,
        formItemName,
        onChangeFormItemValue,
    }: LatinFormItemProps) => {
        const {
            control,
            handleMultipleErrors,
            trigger,
            getValues,
            setFieldElement,
        } = useFormContext<OrderFormValues>();
        const fieldHandler = new InputFieldEventHandler<OrderFormValues>(
            trigger,
        );

        const getItemLatin = (): string => {
            const values = getValues();
            return values[formItemName];
        };

        const maxLengthValidate = (): string => {
            const itemLatin = getItemLatin();
            const [value, message] = getFIOMaxLengthValidation({
                maxLength: MAX_LENGTH_LATIN,
            });

            return itemLatin.length > value ? message : null;
        };

        const minLengthValidate = (): string => {
            const itemLatin = getItemLatin();
            const [value, message] = getFIOMinLengthValidation({
                minLength: MIN_LENGTH_LATIN,
            });

            return itemLatin.length < value ? message : null;
        };

        const FIOAllowedCharactersValidation = (): string => {
            const itemLatin = getItemLatin();
            const [value, message] =
                getFIOAllowedCharactersForFirstAndLastSymbolValidation();

            return value.test(itemLatin) ? null : message;
        };

        return (
            <Controller<OrderFormValues>
                name={formItemName}
                control={control}
                rules={{
                    required: ValidationRules.required(),
                    pattern: ValidationRules.allowedCharacters(
                        ...getFIOAllowedCharactersValidation(),
                    ),
                    validate: {
                        minLength: minLengthValidate,
                        maxLength: maxLengthValidate,
                        validate: FIOAllowedCharactersValidation,
                    },
                }}
                defaultValue={defaultValue}
                render={({ field, fieldState }) => {
                    const { onBlur, onClear, onChange } =
                        fieldHandler.getEventHandlers(
                            field,
                            formItemName,
                            (value) => {
                                onChangeFormItemValue(removeSpaces(value));
                            },
                        );

                    return (
                        <Input
                            clear
                            shouldUpperCase
                            ref={(element) => {
                                field.ref(element);
                                setFieldElement(formItemName, element);
                            }}
                            label={placeholder}
                            inputClassName={classNames(
                                formItemName,
                                WEB_VISOR_DISABLE,
                            )}
                            size="l"
                            value={field.value as string}
                            error={handleMultipleErrors(fieldState.error)}
                            onBlur={(event) =>
                                onBlur({
                                    ...event,
                                    target: {
                                        ...event.target,
                                        value: removeSpaces(event.target.value),
                                    },
                                })
                            }
                            onClear={onClear}
                            onChange={onChange}
                        />
                    );
                }}
            />
        );
    },
);
